import { useEffect, useRef, useCallback } from 'react';

export const useGooglePlacesAutocomplete = (inputRef, options = {}) => {
  const autocompleteRef = useRef(null);
  const { onPlaceSelect, componentRestrictions = { country: 'us' } } = options;

  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions,
      fields: ['formatted_address', 'address_components', 'geometry', 'place_id']
    });

    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (place && place.address_components) {
        const addressData = parseAddressComponents(place);
        if (onPlaceSelect) {
          onPlaceSelect(addressData);
        }
      }
    });

    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [inputRef, onPlaceSelect, componentRestrictions]);

  return autocompleteRef.current;
};

const parseAddressComponents = (place) => {
  const components = place.address_components || [];
  
  const getComponent = (type, useShort = false) => {
    const component = components.find(c => c.types.includes(type));
    return component ? (useShort ? component.short_name : component.long_name) : '';
  };

  const streetNumber = getComponent('street_number');
  const route = getComponent('route');
  
  return {
    formatted_address: place.formatted_address || '',
    street_address: `${streetNumber} ${route}`.trim(),
    city: getComponent('locality') || getComponent('sublocality_level_1') || getComponent('administrative_area_level_2'),
    state: getComponent('administrative_area_level_1', true),
    zip: getComponent('postal_code'),
    country: getComponent('country', true),
    latitude: place.geometry?.location?.lat() || null,
    longitude: place.geometry?.location?.lng() || null,
    place_id: place.place_id || ''
  };
};

export default useGooglePlacesAutocomplete;
